import { Banner, Button, Checkbox, Dialog, Field, Input, InputArea } from '@cloudflare/kumo';
import { useEffect, useState } from 'react';
import { funnelApi, type Deal, type FunnelActivity, type FunnelTask } from '../../services/funnelApi';

type Props = {
  deal: Deal;
  onClose: () => void;
};

const errMsg = (e: unknown, fallback: string) => (e instanceof Error && e.message ? e.message : fallback);

/**
 * Modal com detalhe do card, lista de tarefas e histórico.
 * Carrega `/tasks` e `/activities` em paralelo ao abrir.
 */
export function DealDetail({ deal, onClose }: Props) {
  const [tasks, setTasks] = useState<FunnelTask[]>([]);
  const [activities, setActivities] = useState<FunnelActivity[]>([]);
  const [newTitle, setNewTitle] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [noteText, setNoteText] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    Promise.all([funnelApi.tasks(deal.id), funnelApi.activities(deal.id)])
      .then(([t, a]) => {
        if (!mounted) return;
        setTasks(t.tasks ?? []);
        setActivities(a.activities ?? []);
      })
      .catch((e) => mounted && setErr(e?.message ?? 'Falha ao carregar'));
    return () => { mounted = false; };
  }, [deal.id]);

  const handleAddTask = async () => {
    if (!newTitle.trim()) return;
    setBusy(true);
    setErr(null);
    try {
      const res = await funnelApi.createTask(deal.id, { title: newTitle.trim(), description: newDescription.trim() || null });
      setTasks((prev) => [...prev, res.task]);
      setNewTitle('');
      setNewDescription('');
    } catch (e: unknown) {
      setErr(errMsg(e, 'Erro ao criar tarefa'));
    } finally {
      setBusy(false);
    }
  };

  const handleToggleTask = async (task: FunnelTask) => {
    setBusy(true);
    try {
      const done = task.doneAt ? null : new Date().toISOString();
      const res = await funnelApi.updateTask(task.id, { doneAt: done });
      setTasks((prev) => prev.map((t) => (t.id === task.id ? res.task : t)));
    } catch (e: unknown) {
      setErr(errMsg(e, 'Erro ao atualizar tarefa'));
    } finally {
      setBusy(false);
    }
  };

  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    setBusy(true);
    try {
      const res = await funnelApi.addActivity(deal.id, 'note', { text: noteText.trim() });
      setActivities((prev) => [...prev, res.activity]);
      setNoteText('');
    } catch (e: unknown) {
      setErr(errMsg(e, 'Erro ao salvar nota'));
    } finally {
      setBusy(false);
    }
  };

  const handleMarkWon = async () => {
    onClose();
  };

  return (
    <Dialog.Root open={true} onOpenChange={(o: boolean) => { if (!o) (onClose)(); }}>
<Dialog size="lg" className="p-6">
<Dialog.Title className="mb-4 text-lg font-semibold">{<h2 className="text-xl font-semibold" data-testid="funil-detail-title">{deal.title}</h2>}</Dialog.Title>

      <div data-testid="funil-detail" className="max-h-[75vh] overflow-y-auto pr-1">
        <header className="funil-detail-header">
          <div>
            <div className="text-kumo-subtle">Pipeline: {deal.pipeline?.name ?? deal.pipelineId}</div>
            <div className="text-kumo-subtle">Estágio atual: {deal.stage?.name ?? deal.stageId}</div>
          </div>
          <Button variant="secondary" onClick={onClose} data-testid="funil-detail-close">
            Fechar
          </Button>
        </header>

        <div className="funil-detail-body">
          {err ? <Banner variant="error" data-testid="funil-detail-error" description={<>{err}</>} /> : null}

          <section className="funil-detail-section">
            <h3 className="text-lg font-semibold">Resumo</h3>
            <div className="funil-detail-row"><span className="text-xs text-kumo-subtle">Valor</span><span>{Number(deal.amount).toLocaleString('pt-BR', { style: 'currency', currency: deal.currency || 'BRL' })}</span></div>
            <div className="funil-detail-row"><span className="text-xs text-kumo-subtle">Contato</span><span>{deal.contact?.nome ?? '—'}</span></div>
            <div className="funil-detail-row"><span className="text-xs text-kumo-subtle">Responsável</span><span>{deal.owner?.nome ?? '—'}</span></div>
            <div className="funil-detail-row"><span className="text-xs text-kumo-subtle">Origem</span><span>{deal.origin ?? '—'}</span></div>
            <div className="funil-detail-row"><span className="text-xs text-kumo-subtle">Fechamento previsto</span><span>{deal.expectedCloseAt ? new Date(deal.expectedCloseAt).toLocaleDateString('pt-BR') : '—'}</span></div>
          </section>

          <section className="funil-detail-section">
            <h3 className="text-lg font-semibold">Tarefas</h3>
            {tasks.length === 0 ? (
              <div className="text-kumo-subtle funil-empty">Nenhuma tarefa</div>
            ) : (
              <ul className="funil-task-list" data-testid="funil-task-list">
                {tasks.map((t) => (
                  <li key={t.id} className={`funil-task${t.doneAt ? ' done' : ''}`} data-testid={`funil-task-${t.id}`}>
                    <Checkbox onCheckedChange={() => handleToggleTask(t)} aria-label={`Marcar tarefa ${t.title}`} checked={!!t.doneAt} disabled={busy} />
                    <span className="funil-task-title">{t.title}</span>
                    {t.dueAt ? <span className="text-xs text-kumo-subtle due">{new Date(t.dueAt).toLocaleDateString('pt-BR')}</span> : null}
                  </li>
                ))}
              </ul>
            )}
            <div className="funil-form-row">
              <Field label="Nova tarefa">
                <Input onChange={(e) => setNewTitle(e.target.value)} value={newTitle} placeholder="Ligar para o cliente…" disabled={busy} id={'funil-new-task'} data-testid={'funil-new-task-title'} />
              </Field>
              <Field label="Detalhes da tarefa" description="Opcional">
                <Input onChange={(e) => setNewDescription(e.target.value)} value={newDescription} placeholder="Detalhes (opcional)" disabled={busy} />
              </Field>
            </div>
            <div className="funil-form-actions">
              <Button variant="primary" onClick={handleAddTask} disabled={busy || !newTitle.trim()} data-testid="funil-add-task">
                Adicionar tarefa
              </Button>
            </div>
          </section>

          <section className="funil-detail-section">
            <h3 className="text-lg font-semibold">Histórico</h3>
            <div className="funil-activity" data-testid="funil-activity-list">
              {activities.length === 0 ? (
                <div className="text-kumo-subtle funil-empty">Sem atividades</div>
              ) : (
                activities.map((a) => (
                  <div className="funil-activity-item" key={a.id}>
                    <span className="font-semibold">{a.kind}</span>
                    <span className="text-xs text-kumo-subtle funil-activity-payload">
                      {JSON.stringify(a.payload ?? {}, null, 0)}
                    </span>
                    <span className="text-xs text-kumo-subtle">
                      <time dateTime={a.createdAt}>{new Date(a.createdAt).toLocaleString('pt-BR')}</time>
                    </span>
                  </div>
                ))
              )}
            </div>
            <div className="funil-form-row">
              <InputArea label="Adicionar nota" onChange={(e) => setNoteText(e.target.value)} rows={2} value={noteText} disabled={busy} id="funil-new-note" data-testid="funil-new-note" />
            </div>
            <div className="funil-form-actions">
              <Button variant="primary" onClick={handleAddNote} disabled={busy || !noteText.trim()} data-testid="funil-add-note">
                Salvar nota
              </Button>
            </div>
          </section>

          <section className="funil-detail-section">
            <Button variant="secondary" onClick={handleMarkWon} data-testid="funil-mark-won">
              Fechar painel
            </Button>
          </section>
        </div>
      </div>
    
</Dialog>
</Dialog.Root>
  );
}
